const FORM_VALIDATION_MESSAGE = 'Исправьте ошибки в форме';

const HTTP_MESSAGES = {
  400: 'Некорректные данные',
  401: 'Срок авторизации истёк. Войдите снова',
  403: 'Недостаточно прав для выполнения операции',
  404: 'Объект не найден',
  409: 'Операция конфликтует с текущим состоянием данных',
  422: 'Проверьте правильность заполнения полей',
  500: 'Внутренняя ошибка сервера',
};
const { 422: VALIDATION_FALLBACK_MESSAGE } = HTTP_MESSAGES;

const INTEGER_FIELDS = new Set(['minimum', 'maximum', 'rate_amount', 'year', 'page', 'limit']);
const NUMBER_FIELDS = new Set(['latitude', 'longitude', 'speed', 'battery', 'altitude', 'accuracy']);
const DATE_FIELDS = new Set(['start_date', 'end_date', 'published_date', 'created_at', 'updated_at', 'start_time', 'end_time']);
const UUID_FIELDS = new Set(['id', 'provider_id', 'vehicle_id', 'inspector_id', 'rule_id', 'policy_id', 'geography_id', 'zone_id', 'device_id']);
const ENUM_MESSAGES = {
  rule_type: 'Выберите тип правила',
};

const hasRussianText = value => /[А-Яа-яЁё]/.test(value || '');

const isTechnicalMessage = value => /input should|field required|validation error|invalid input|invalid value|unable to|could not|internal server error|traceback|pydantic|\[object object\]|uuid_parsing|not found/i.test(value || '');

const getStatus = response => {
  const value = response?.httpStatus || response?.status || response?.code
    || response?.error?.httpStatus || response?.error?.status || response?.error?.code;
  const status = Number(value);
  return Number.isFinite(status) ? status : null;
};

const getFieldName = location => {
  const parts = Array.isArray(location) ? location : String(location || '').split('.');
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const value = parts[index];
    if (typeof value === 'string' && value && !['body', 'query', 'path'].includes(value)) {
      return value;
    }
  }
  return '';
};

const getLimitMessage = (prefix, context, fallback) => {
  const value = context?.gt ?? context?.ge ?? context?.lt ?? context?.le;
  return typeof value === 'undefined' ? fallback : `${prefix} ${value}`;
};

export const getValidationMessage = (error = {}, fieldName = '') => {
  const type = String(error.type || '').toLowerCase();
  const rawMessage = String(error.message || error.msg || '');
  const context = error.ctx || error.context || {};

  if (hasRussianText(rawMessage)) {
    return rawMessage.replace(/^Value error,\s*/i, '');
  }
  if (ENUM_MESSAGES[fieldName] && (type.includes('enum') || /input should be ['"]|valid choice|permitted/i.test(rawMessage))) {
    return ENUM_MESSAGES[fieldName];
  }
  if (type.includes('missing') || /field required|missing data|required field/i.test(rawMessage)) {
    return 'Поле обязательно для заполнения';
  }
  if (type.includes('int') || /valid integer|integer.*parse/i.test(rawMessage)) {
    return 'Введите целое число';
  }
  if (type.includes('float') || type.includes('decimal') || type.includes('number') || /valid number|valid float|number.*parse/i.test(rawMessage)) {
    return 'Введите число';
  }
  if (type.includes('bool') || /valid boolean|boolean.*parse/i.test(rawMessage)) {
    return 'Выберите корректное значение';
  }
  if (type.includes('uuid') || /valid uuid|uuid.*parse/i.test(rawMessage) || UUID_FIELDS.has(fieldName)) {
    return 'Выберите корректное значение';
  }
  if (type.includes('enum') || type.includes('literal') || /input should be ['"]|valid choice|permitted/i.test(rawMessage)) {
    return ENUM_MESSAGES[fieldName] || 'Выберите корректное значение';
  }
  if (type.includes('string_too_short') || /at least .* characters|too short/i.test(rawMessage)) {
    return 'Значение слишком короткое';
  }
  if (type.includes('string_too_long') || /at most .* characters|too long/i.test(rawMessage)) {
    return 'Значение слишком длинное';
  }
  if (type.includes('date') || type.includes('time') || /valid date|valid datetime|valid time/i.test(rawMessage) || DATE_FIELDS.has(fieldName)) {
    return 'Введите корректную дату';
  }
  if (type.includes('url') || /valid url|url parsing/i.test(rawMessage)) {
    return 'Введите корректный URL';
  }
  if (type.includes('greater_than_equal')) {
    return getLimitMessage('Значение должно быть не меньше', context, 'Значение меньше допустимого');
  }
  if (type.includes('greater_than')) {
    return getLimitMessage('Значение должно быть больше', context, 'Значение должно быть больше допустимого');
  }
  if (type.includes('less_than_equal')) {
    return getLimitMessage('Значение должно быть не больше', context, 'Значение больше допустимого');
  }
  if (type.includes('less_than')) {
    return getLimitMessage('Значение должно быть меньше', context, 'Значение должно быть меньше допустимого');
  }
  if (type.includes('json') || /json.*invalid/i.test(rawMessage)) {
    return 'Введите корректный JSON';
  }
  if (INTEGER_FIELDS.has(fieldName)) {
    return 'Введите целое число';
  }
  if (NUMBER_FIELDS.has(fieldName)) {
    return 'Введите число';
  }
  return 'Проверьте значение поля';
};

const collectValidationErrors = response => {
  const payload = response?.error && typeof response.error === 'object' ? response.error : response;
  const candidates = [
    payload?.data,
    payload?.detail,
    payload?.error?.data,
    response?.data,
    response?.detail,
  ];
  const errors = candidates.find(Array.isArray) || [];
  const mdsErrors = Array.isArray(payload?.error_details) ? payload.error_details : [];
  const mappedMdsErrors = mdsErrors.map(item => ({
    loc: item.field,
    message: item.message,
    type: item.type,
    ctx: item.ctx,
  }));
  const objectErrors = payload?.errors && typeof payload.errors === 'object' && !Array.isArray(payload.errors)
    ? Object.entries(payload.errors).flatMap(([field, messages]) => (Array.isArray(messages) ? messages : [messages])
      .map(message => ({ loc: [field], message })))
    : [];
  return [...errors, ...mappedMdsErrors, ...objectErrors].filter(item => item && typeof item === 'object');
};

const getCandidateMessage = response => {
  const payload = response?.error && typeof response.error === 'object' ? response.error : response;
  const candidates = [
    typeof response === 'string' ? response : '',
    typeof payload?.message === 'string' ? payload.message : '',
    typeof payload?.detail === 'string' ? payload.detail : '',
    typeof payload?.error_description === 'string' ? payload.error_description : '',
    typeof response?.error === 'string' ? response.error : '',
    response instanceof Error ? response.message : '',
  ];
  return candidates.find(Boolean) || '';
};

const isNetworkError = (response, candidate) => (
  response?.status === 'CONNECTION_ERROR'
  || response?.code === 'CONNECTION_ERROR'
  || /failed to fetch|networkerror|network request failed|connection/i.test(candidate || '')
);

const fallbackMessage = (status, defaultMessage) => {
  if (status >= 500) return HTTP_MESSAGES[500];
  return HTTP_MESSAGES[status] || defaultMessage || 'Не удалось выполнить операцию';
};

export const normalizeApiError = (response, { defaultMessage = '' } = {}) => {
  const status = getStatus(response);
  const validationErrors = collectValidationErrors(response);
  const fieldErrors = validationErrors.reduce((result, item) => {
    const fieldName = getFieldName(item.loc || item.field);
    if (fieldName && !result[fieldName]) {
      result[fieldName] = getValidationMessage(item, fieldName);
    }
    return result;
  }, {});
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const candidate = getCandidateMessage(response);
  const networkError = isNetworkError(response, candidate);
  let message;

  if (hasFieldErrors) {
    message = FORM_VALIDATION_MESSAGE;
  } else if (networkError) {
    message = 'Проблема с подключением к сети';
  } else if (hasRussianText(candidate) && !isTechnicalMessage(candidate)) {
    message = candidate;
  } else if (status === 422 || validationErrors.length) {
    message = VALIDATION_FALLBACK_MESSAGE;
  } else {
    message = fallbackMessage(status, defaultMessage);
  }

  return {
    status,
    message,
    fieldErrors,
    hasFieldErrors,
    isValidation: hasFieldErrors || status === 422 || validationErrors.length > 0,
  };
};

export { FORM_VALIDATION_MESSAGE };
